

































































































































import Vue from 'vue';
import axios from 'axios';
import { mapState } from 'vuex';

export default Vue.extend({
    name: 'App',
    components: {
        
    },
    data() {
        return {
            showMenu: true,
            showSubMenu: false,
            hideOverflow: true,
            connectingOAuth: false,
            popupBlockedError: false,
            reconnectDismissed: false,
            oauthPopup: null as Window | null,
            oauthPopupUrl: '',
            oauthPopupTimer: null as number | null
        }
    },
    created() {
      window.alert = (message) => {
        this.$store.state.errorMsg = message;
        this.$store.state.showErrorMsg = true;
      }
      window.addEventListener('message', this.handleOAuthMessage);
      this.checkSession();
    },
    mounted() {
      document.addEventListener('click', this.handleOutsideMenuClick);
    },
    beforeDestroy() {
      window.removeEventListener('message', this.handleOAuthMessage);
      document.removeEventListener('click', this.handleOutsideMenuClick);
      this.clearPopupWatcher();
    },
    methods: {
        toggleMenu() {
          this.showMenu = !this.showMenu;
        },
        accountLabel(acc: any): string {
          return acc.AccountName || `Account ${acc.HSPortalId}`;
        },
        handleOutsideMenuClick(event: MouseEvent) {
          if(!this.showSubMenu) return;
          const topBar = (this.$el as Element).querySelector('.top-bar');
          if(topBar && !topBar.contains(event.target as Node)) {
            this.showSubMenu = false;
          }
        },
        checkSession() {
          // Stashed on the store instance (not store.state - it's a Promise,
          // not serializable app state) so the router guard can always await
          // the MOST RECENT check rather than just the first one. sessionChecked
          // alone only protects the initial page-load race; once true, it never
          // resets, so a guard decision made right after switching accounts
          // could run before this new check updates accountRole.
          const promise = axios.get(`${this.$store.state.apiUrl}/session`)
            .then(resp => {
              this.$store.state.isSystemAdmin = !!resp.data.isSystemAdmin;
              this.$store.state.userId = resp.data.userId;
              this.$store.state.portalId = resp.data.portalId;
              this.$store.state.tier = resp.data.tier;
              this.$store.state.accountRole = resp.data.accountRole || null;
              this.$store.state.linkedAccounts = resp.data.accounts || [];
              this.$store.state.needsReconnect = !!resp.data.needsReconnect;
              this.connectingOAuth = false;
              this.popupBlockedError = false;
              if(resp.data.isSystemAdmin && !resp.data.portalId) {
                // Admins aren't personally linked to accounts like regular
                // users - they browse/pick one from the admin dashboard
                // (View As), not the account-picker overlay below.
                this.$router.push('/admin');
              } else if(!resp.data.userId && !resp.data.isSystemAdmin && !resp.data.hasPaid) {
                // A userId session with no portalId yet (0 or 2+ linked
                // accounts) needs to pick an account first - the
                // account-picker overlay handles that, so don't also bounce
                // to /payment or HubSpot.
                this.$router.push('/payment');
              }
              this.$store.state.sessionChecked = true;
            })
            .catch(e => {
              // No valid session (e.g. just logged out) - clear whatever was
              // left over from before, so stale state doesn't leak into the
              // nav/gating until a full page reload happens to reset it.
              this.$store.state.isSystemAdmin = false;
              this.$store.state.userId = null;
              this.$store.state.portalId = null;
              this.$store.state.accountRole = null;
              this.$store.state.linkedAccounts = [];
              this.$store.state.needsReconnect = false;
              this.$store.state.sessionChecked = true;
              // These auth-flow pages have no session yet either - let them
              // show their own forms instead of bouncing to /login. Router
              // uses hash mode, so the route lives in location.hash, not
              // location.pathname.
              const path = window.location.hash.replace(/^#/, '');
              if(path === '/admin' || path === '/login' || path.startsWith('/accept-invite/') || path.startsWith('/reset-password/')) {
                return;
              }
              this.$router.push('/login');
            });
          (this.$store as any)._sessionCheckPromise = promise;
          return promise;
        },
        openOAuthPopup(url?: string) {
          this.oauthPopupUrl = url || `${this.$store.state.apiUrl}/oauth`;
          if(this.oauthPopup && !this.oauthPopup.closed) {
            this.oauthPopup.focus();
            return;
          }
          this.popupBlockedError = false;
          this.connectingOAuth = true;

          const width = 600, height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;
          this.oauthPopup = window.open(
            this.oauthPopupUrl,
            'oauth-popup',
            `width=${width},height=${height},left=${left},top=${top}`
          );

          if(!this.oauthPopup) {
            this.connectingOAuth = false;
            this.popupBlockedError = true;
            return;
          }

          this.watchPopupClosed();
        },
        watchPopupClosed() {
          this.clearPopupWatcher();
          this.oauthPopupTimer = window.setInterval(() => {
            if(this.oauthPopup && this.oauthPopup.closed) {
              this.clearPopupWatcher();
              // Closed without completing - just quietly reset. No dead-end
              // "try again" prompt needed since the user can always click
              // Log In with HubSpot again themselves.
              this.connectingOAuth = false;
              this.oauthPopup = null;
            }
          }, 500);
        },
        clearPopupWatcher() {
          if(this.oauthPopupTimer) {
            window.clearInterval(this.oauthPopupTimer);
            this.oauthPopupTimer = null;
          }
        },
        handleOAuthMessage(event: MessageEvent) {
          const expectedOrigin = new URL(this.$store.state.apiUrl).origin;
          if(event.origin !== expectedOrigin) return;
          const validSources = ['hubspot-pdf-oauth', 'google-pdf-oauth'];
          if(!event.data || validSources.indexOf(event.data.source) === -1) return;

          this.clearPopupWatcher();
          this.connectingOAuth = false;
          if(this.oauthPopup && !this.oauthPopup.closed) {
            this.oauthPopup.close();
          }
          this.oauthPopup = null;

          if(event.data.status === 'success') {
            this.checkSession();
            // Both flows always resolve to exactly one identity/portal - no
            // account picker to wait on, so head to the homepage right away
            // instead of leaving them sitting on /login.
            this.$router.push('/').catch(() => undefined);
          } else {
            window.alert(event.data.message || 'There was a problem signing in. Please try again.');
          }
        },
        retryOAuth() {
          this.popupBlockedError = false;
          this.openOAuthPopup(this.oauthPopupUrl);
        },
        logout() {
          this.showSubMenu = false;
          axios.get(`${this.$store.state.apiUrl}/signout`)
            .finally(() => {
              this.checkSession();
            });
        },
        exitImpersonation() {
          axios.post(`${this.$store.state.apiUrl}/admin/exit-impersonation`)
            .then(() => {
              window.location.href = '/admin';
            });
        },
        switchAccount(portalId: any) {
          axios.post(`${this.$store.state.apiUrl}/auth/switch-account`, { portalId })
            .then(() => {
              this.showSubMenu = false;
              this.checkSession();
            })
            .catch(e => {
              window.alert(e.response ? e.response.data : 'There was an error switching accounts.');
            });
        }
    },
    computed: {
      ...mapState(['routeLoaded']),
      canManageAccount(): boolean {
        return this.$store.state.accountRole === 'AccountManager' || this.$store.state.isSystemAdmin;
      },
      currentAccountLabel(): string {
        const acc = (this.$store.state.linkedAccounts || []).find((a: any) => String(a.HSPortalId) === String(this.$store.state.portalId));
        return acc ? this.accountLabel(acc) : this.$store.state.portalId;
      }
    },
    watch: {
      $route (){
        this.$store.state.routeLoaded = false;
      },
      routeLoaded(newVal, oldVal) {
          if(newVal) {
            setTimeout(() => { this.hideOverflow = false; }, 600)
          } else {
            this.hideOverflow = true;
          }
      }
    }
})
